import { useEffect, useState, useMemo, useCallback } from "react";
import { Button, Table, Stack, Badge, Form, Modal } from "react-bootstrap";
import { useMediaQuery } from "react-responsive";

import supabase from "../utils/Supabase";
import { showToast } from "../utils/Toasts";
import { insertNewUser } from "../utils/UserUtil";
import { exportCsv } from "../utils/ExportCsv";
import { formatDateTime, formatRelativeTime } from "../utils/Dates";

import PageList from "../components/PageList";
import { LoadingRing } from "../components/LoadingRing";
import { LoadingText } from "../components/LoadingText";
import { ConfirmationModal } from "../components/Confirmation";

import { PatreonLogo } from "../components/PatreonLogo";
import {
    BENEFIT_REGISTRY,
    BenefitMap,
    BenefitValue,
    getBenefitDefaults,
    getDefaultBenefits,
    getDisplayedBenefits,
    parseBenefitsFromRpc,
    saveUserBenefitOverrides,
} from "../utils/Benefits";

const patreonConfigured = !!process.env.REACT_APP_PATREON_CLIENT_ID;

const USERS_PER_PAGE = 100;

function RelativeTimestamp({ value }: { value?: string | null }) {
  if (!value) {
    return null;
  }

  return (
    <span title={formatDateTime(value)}>
      {formatRelativeTime(value)}
    </span>
  );
}

function formatPatronStatus(status) {
  if (status === "active_patron") {
    return "Active Patron";
  }
  if (status === "former_patron") {
    return "Former Patron";
  }
  if (status === "declined_patron") {
    return "Declined Patron";
  }
  return "Never Subscribed";
}

const INITIAL_FILTER_VALUES = {
  admin: false,
  indexer: false,
  tagger: false,
  featureAdmin: false,
  patreonLinked: false,
  curator: false,
  blacklisted: false,
  email: "",
};

function UserAdminDashboard() {
  const isSmall = useMediaQuery({ query: '(max-width: 576px)' });
  const [allUsers, setAllUsers] = useState([]);
  const [hasUserData, setHasUserData] = useState(false);
  const [dirty, setDirty] = useState(new Set());
  const [filters, setFilters] = useState({ ...INITIAL_FILTER_VALUES });
  const [saving, setSaving] = useState(false);
  const [sortDirection, setSortDirection] = useState("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [shownUsers, setShownUsers] = useState([]);

  const [confirmationMessage, setConfirmationMessage] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [onConfirm, setOnConfirm] = useState(() => () => {});
  const [onHide, setOnHide] = useState(() => () => {});
    const [benefitDefaults, setBenefitDefaults] = useState<BenefitMap>(getDefaultBenefits());
    // Edit popup: local overrides only. "unset" means inherit from Patreon or default.
    const [editingUser, setEditingUser] = useState(null);
  const [editDraft, setEditDraft] = useState<Record<string, BenefitValue | "" | "unset">>({});
  const [savingOverrides, setSavingOverrides] = useState(false);

  function parseUserBenefits(user) {
    // Same helper the rest of the site uses — tags must match real access.
    const benefits = parseBenefitsFromRpc(user.benefits);
    return {
      ...user,
      benefits,
      benefit_overrides: user.benefit_overrides && typeof user.benefit_overrides === "object"
        ? user.benefit_overrides
        : {},
    };
  }

  async function getUserData() {
    const { data, error } = await supabase.rpc('get_user_auth');

    if (!data || error) {
      return;
    }

    let users = data;

    if (patreonConfigured) {
      const { data: patreonUsers, error: patreonError } = await supabase
        .from("patreon_users")
        .select("user_id, patron_email, patron_status, tier_title, valid_tokens");

      if (!patreonError && patreonUsers) {
        const patreonByUserId = {};
        for (const row of patreonUsers) {
          patreonByUserId[row.user_id] = row;
        }
        users = data.map((user) => {
          const patreon = patreonByUserId[user.user_id];
          if (!patreon) {
            return user;
          }
          return {
            ...user,
            patron_email: patreon.patron_email,
            patron_status: patreon.patron_status,
            tier_title: patreon.tier_title,
            valid_tokens: patreon.valid_tokens,
          };
        });
      }
    }

    setAllUsers(users.map(parseUserBenefits));
    setHasUserData(true);
  }

  async function updateUsers() {
    // Build the payload from allUsers, not filteredUsers: toggling a checkbox
    // rewrites allUsers, which re-runs the filter, so an edit that clears the
    // active filter drops the row out of filteredUsers before we can save it.
    // Unchecking "Blacklisted" while the Blacklisted filter was on silently
    // discarded the change, which is why unblacklisted users kept coming back.
    const dirtyUsers = allUsers.filter((user) => dirty.has(user.user_id));
    const updatePayload = dirtyUsers
      .map((user) => ({
        userDataId: user.userdata_id,
        isIndexer: user.isIndexer,
        isCurator: user.isCurator,
        isApprover: user.isApprover,
        isBlacklisted: user.isBlacklisted,
        isTagger: user.isTagger,
        isFeatureAdmin: user.isFeatureAdmin
      }));

    const numUpdates = updatePayload.length;

    if (numUpdates === 0) {
      showToast("info", "No changes to save");
      setSaving(false);
      return;
    }

    const { data, error } = await supabase.rpc("admin_set_user_permissions", {
      updates: updatePayload
    });

    if (error || !data) {
      showToast("error", "Error updating users");
      console.log("Error updating users:", error);
      setSaving(false);
      return;
    }

    if (data.length > 0) {
      showToast(
        "success",
        `Successfully updated ${data.length} user${data.length > 1 ? "s" : ""}`
      );
      await getUserData();
      setDirty(new Set());
      setFilters({ ...INITIAL_FILTER_VALUES });
    }
    setSaving(false);
  }

  const changeRole = useCallback(
    (userId: string, targetedRole: string, targetedRoleValue: boolean) => {
      const userChanges = allUsers.map((userRow) => {
        if (userId !== userRow.user_id) {
          return userRow;
        }

        return {
          ...userRow,
          isIndexer:
            targetedRole === "indexer" ? targetedRoleValue : userRow.isIndexer,
          isCurator:
            targetedRole === "curator" ? targetedRoleValue : userRow.isCurator,
          isApprover:
            targetedRole === "approver"
              ? targetedRoleValue
              : userRow.isApprover,
          isBlacklisted:
            targetedRole === "blacklisted"
              ? targetedRoleValue
              : userRow.isBlacklisted,
          isTagger:
            targetedRole === "tagger" ? targetedRoleValue : userRow.isTagger,
          isFeatureAdmin:
            targetedRole === "featureAdmin"
              ? targetedRoleValue
              : userRow.isFeatureAdmin,
        };
      });

      setAllUsers(userChanges);
    },
    [allUsers]
  );

  function openBenefitEditor(user) {
    // Seed the popup from raw local overrides, not from the resolved tags.
    // Missing keys are unset (inherit), including an explicit Off override.
    const draft: Record<string, BenefitValue | "unset"> = {};
    const overrides = user.benefit_overrides || {};
    for (const benefit of BENEFIT_REGISTRY) {
      if (Object.prototype.hasOwnProperty.call(overrides, benefit.id)
        && overrides[benefit.id] !== null
        && overrides[benefit.id] !== undefined) {
        draft[benefit.id] = benefit.valueType === "boolean"
          ? overrides[benefit.id] === true
          : overrides[benefit.id];
      } else {
        draft[benefit.id] = "unset";
      }
    }
    setEditingUser(user);
    setEditDraft(draft);
  }

  async function saveBenefitEditor() {
    if (!editingUser) {
      return;
    }

    const overrides: Record<string, BenefitValue | null> = {};
    for (const benefit of BENEFIT_REGISTRY) {
      const value = editDraft[benefit.id];
      if (value === "unset" || value === "") {
        // null tells the database to delete that override so the benefit inherits again.
        overrides[benefit.id] = null;
      } else {
        overrides[benefit.id] = value as BenefitValue;
      }
    }

    setSavingOverrides(true);
    const resolved = await saveUserBenefitOverrides(editingUser.user_id, overrides);
    setSavingOverrides(false);

    if (!resolved) {
      showToast("error", "Unable to save local benefits.");
      return;
    }

    const nextOverrides = {};
    for (const benefit of BENEFIT_REGISTRY) {
      const value = editDraft[benefit.id];
      if (value !== "unset" && value !== "") {
        nextOverrides[benefit.id] = value;
      }
    }

    setAllUsers((users) => users.map((row) => (
      row.user_id === editingUser.user_id
        // Close the popup by clearing editingUser; tags update from the new resolved map.
        ? { ...row, benefits: resolved, benefit_overrides: nextOverrides }
        : row
    )));
    setEditingUser(null);
    showToast("success", "Local benefits saved.");
  }

  useEffect(() => {
    getUserData();
    getBenefitDefaults().then((defaults) => {
      if (defaults) {
        setBenefitDefaults(defaults);
      }
    });
  }, []);

  const filteredUsers = useMemo(() => {
    const emailFilterUpper = filters.email.toUpperCase();

    let filtered = allUsers.filter((user) => {
      if (emailFilterUpper) {
        const siteEmail = user.email?.toUpperCase() ?? "";
        const patronEmail = user.patron_email?.toUpperCase() ?? "";
        if (!siteEmail.includes(emailFilterUpper) && !patronEmail.includes(emailFilterUpper)) {
          return false;
        }
      }
      if (filters.admin && !user.isApprover) return false;
      if (filters.indexer && !user.isIndexer) return false;
      if (filters.patreonLinked && !user.patreon_linked) return false;
      if (filters.curator && !user.isCurator) return false;
      if (filters.tagger && !user.isTagger) return false;
      if (filters.featureAdmin && !user.isFeatureAdmin) return false;
      if (filters.blacklisted && !user.isBlacklisted) return false;
      return true;
    });

    setCurrentPage(1);
    return filtered;
  }, [filters, allUsers]);

  const sortedUsers = useMemo(() => {
    let sorted = [...filteredUsers];
    // Apply sorting based on `auth_created_at`
    if (sortDirection === "asc") {
      sorted.sort(
        (a, b) =>
          new Date(a.auth_created_at).getTime() - new Date(b.auth_created_at).getTime()
      );
    } else if (sortDirection === "desc") {
      sorted.sort(
        (a, b) =>
          new Date(b.auth_created_at).getTime() - new Date(a.auth_created_at).getTime()
      );
    }
    return sorted;
  }, [filteredUsers, sortDirection]);

  useEffect(() => {
    const users = sortedUsers;
    setShownUsers(users.slice((currentPage - 1) * USERS_PER_PAGE, currentPage * USERS_PER_PAGE));
  }, [currentPage, sortedUsers]);

  return (
    <>
      <Stack direction="horizontal" gap={3} className="flex-wrap">
        <div className="d-flex flex-wrap gap-3 flex-grow-1">
          {[
            ["Admin", "admin"],
            ["Indexer", "indexer"],
            ["Tagger", "tagger"],
            ["Feature Admin", "featureAdmin"],
            ["Patreon Linked", "patreonLinked"],
            ["Curator", "curator"],
            ["Blacklisted", "blacklisted"],
          ].map(([label, key]) => (
            <div key={key}>
              <input
                type="checkbox"
                id={`${key}-user-filter`}
                checked={filters[key]}
                onChange={(e) =>
                  setFilters({ ...filters, [key]: e.target.checked })
                }
                className="m-0"
              />
              <label htmlFor={`${key}-user-filter`} className="ms-2">
                {label}
              </label>
            </div>
          ))}
        </div>

        <div className="d-flex align-items-center flex-grow-1 text-nowrap">
          <label htmlFor="email-users-filter" className="me-2">
            Search email:
          </label>
          <input
            className="form-control ms-1 mt-0 mb-0"
            id="email-users-filter"
            type="text"
            value={filters.email}
            placeholder="example@email.com"
            onChange={(e) =>
              setFilters({ ...filters, email: e.target.value })
            }
          />
        </div>

        <div className="d-flex align-items-center flex-grow-1 text-nowrap">
          <label htmlFor="sort-date" className="me-2">
            Sort by Date:
          </label>
          <select
            id="sort-date"
            className="form-select"
            value={sortDirection}
            onChange={(e) =>
              setSortDirection(e.target.value as "asc" | "desc" | "")
            }
          >
            <option value="asc">Oldest First</option>
            <option value="desc">Newest First</option>
          </select>
        </div>

        <div className="flex-grow-1 text-end">
          <Button
            onClick={async () => {
              setSaving(true);
              await updateUsers();
            }}
          >
            {saving ? (
              <LoadingText text="Saving..." className="mb-0" />
            ) : (
              "Save Changes"
            )}
          </Button>
        </div>
      </Stack>
      <Stack direction={isSmall? "vertical" : "horizontal"} gap={isSmall ? 1 : 3} className="flex-wrap">
        <div className={`my-2 my-md-4 ${isSmall ? 'text-center' : ''}`}>
          {hasUserData ? (
            <>
              <strong>{shownUsers.length}</strong> out of <strong>{allUsers.length}</strong> users shown
            </>
          ) : (
            <LoadingText text="Loading user list..." className="mb-0" />
          )}
        </div>
        <PageList
          className={`ms-md-auto my-2 my-md-4 ${isSmall ? 'justify-content-center' : ''}`}
          totalCount={filteredUsers.length}
          itemsPerPage={USERS_PER_PAGE}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          hideOnEmpty={false}
        />
        <Button
          variant="outline-secondary"
          className="my-2 my-md-4"
          size="sm"
          disabled={sortedUsers.length === 0}
          onClick={() => {
            let usersToExport = sortedUsers;
            usersToExport = usersToExport.map((u) => {
              return {
                "Email": u.email,
                "Status": u.banned_until !== null ? "Banned" :
                  u.userdata_created_at ?
                    u.isActive ? "Active" : "Inactive"
                    : u.email_confirmed_at ? "Confirmed"
                      : "Registered",
                "Registered": u.auth_created_at ? new Date(u.auth_created_at).toLocaleDateString() : 'N/A',
                "Activated": u.userdata_created_at ? new Date(u.userdata_created_at).toLocaleDateString() : 'N/A',
                "Last Login": u.last_sign_in_at && u.userdata_created_at ? new Date(u.last_sign_in_at).toLocaleDateString() : 'N/A',
                "Blacklisted": u.userdata_created_at ? u.isBlacklisted ? "Yes" : "No" : 'N/A',
                "Admin": u.userdata_created_at ? u.isApprover ? "Yes" : "No" : 'N/A',
                "Curator": u.userdata_created_at ? u.isCurator ? "Yes" : "No" : 'N/A',
                "Indexer": u.userdata_created_at ? u.isIndexer ? "Yes" : "No" : 'N/A',
                "Tagger": u.userdata_created_at ? u.isTagger ? "Yes" : "No" : 'N/A',
                "Feature Admin": u.userdata_created_at ? u.isFeatureAdmin ? "Yes" : "No" : 'N/A',
                "Patreon Linked": u.userdata_created_at ? u.patreon_linked ? "Yes" : "No" : 'N/A',
                "Patreon Email": u.patron_email ?? 'N/A',
                "Patreon Status": u.patreon_linked ? formatPatronStatus(u.patron_status) : 'N/A',
                "Patreon Tier": u.tier_title ?? 'N/A',
              };
            });
            exportCsv('users', usersToExport);
          }}
        >
          Export CSV
        </Button>
      </Stack>

      {hasUserData ? (
        <div className="table-responsive user-admin-table-wrapper mt-3">
          <Table striped bordered hover size="sm" className="mb-0">
            <thead>
              <tr>
                <th title="Site email, plus Patreon email and tier when the account is linked.">User</th>
                <th style={{ width: "100px" }} title="Registered (unverified), Confirmed, Active, Inactive, or Banned.">Status</th>
                <th style={{ width: "150px" }} title="When this account was created.">Registered</th>
                <th style={{ width: "150px" }} title="When this user last signed in. Empty until the account is activated.">Last Login</th>
                <th title="Staff permissions: Admin, Indexer, Curator, Tagger, and Feature Admin. Click Save Changes to apply.">Roles</th>
                <th title="Benefits above the site default. Green is from Patreon; blue is a local override.">Benefits</th>
                <th title="Blacklist this user to restrict their access. Click Save Changes to apply.">Moderation</th>
                <th style={{ width: "190px" }} title="Password reset, account activation, or email verification, depending on account state.">Actions</th>
              </tr>
            </thead>
            <tbody>
              {shownUsers.map((user, i) => {
                const userStatus = user.banned_until !== null ? "Banned" :
                  user.userdata_created_at ?
                    user.isActive ? "Active" : "Inactive"
                      : user.email_confirmed_at ? "Confirmed"
                        : "Registered";

                const statusColor = user.banned_until !== null ? "danger" :
                  user.userdata_created_at ?
                    user.isActive ? "success" : "dark"
                      : user.email_confirmed_at ? "info"
                        : "warning";

                const patreonLinked = user.patreon_linked;
                // Extras only, from the same resolved map the site uses. Teal = local, green = Patreon.
                const displayedBenefits = getDisplayedBenefits(user.benefits, benefitDefaults);

                return(
                  <tr
                    key={user.user_id}
                    onChange={() => {
                      if (!dirty.has(user.user_id)) {
                        setDirty((prevDirty) => {
                          const newDirty = new Set(prevDirty);
                          newDirty.add(user.user_id);
                          return newDirty;
                        });
                      }
                    }}
                  >
                    <td className="align-middle">
                      <div>{user.email}</div>
                      {patreonConfigured && patreonLinked && (
                        <div className="user-admin-patreon">
                          <div className="user-admin-patreon-header">
                            <PatreonLogo />
                            Linked
                          </div>
                          <div className="user-admin-patreon-body">
                            {user.patron_email && (
                              <div className="text-break">{user.patron_email}</div>
                            )}
                            <div className="user-admin-patreon-badges">
                              <Badge
                                pill
                                bg={user.tier_title
                                  ? (user.patron_status === "active_patron" ? "success" : "secondary")
                                  : "light"}
                                text={user.tier_title ? undefined : "dark"}
                              >
                                {user.tier_title || ""}
                              </Badge>
                              {/* Patron status badge hidden: the tier badge already uses
                                  patron_status for color, so a second Active/Former/Declined
                                  label was redundant. Uncomment to restore the explicit status.
                              {user.patron_status && (
                                <Badge
                                  pill
                                  bg={user.patron_status === "active_patron" ? "success" : "secondary"}
                                >
                                  {formatPatronStatus(user.patron_status)}
                                </Badge>
                              )}
                              */}
                            </div>
                            {user.valid_tokens === false && (
                              <Badge bg="danger">Needs to unlink/relink.</Badge>
                            )}
                          </div>
                        </div>
                      )}
                    </td>
                    <td className="text-center align-middle">
                      <Badge bg={statusColor}>
                        {userStatus}
                      </Badge>
                    </td>
                    <td className="text-center align-middle">
                      <RelativeTimestamp value={user.auth_created_at} />
                    </td>
                    <td className="text-center align-middle">
                      <RelativeTimestamp
                        value={user.last_sign_in_at && user.userdata_created_at
                          ? user.last_sign_in_at
                          : null}
                      />
                    </td>
                    <td className="ps-2">
                      <Form.Check
                        inline
                        className="mb-0"
                        disabled={!user.userdata_created_at}
                        type="checkbox"
                        label="Admin"
                        key={"appr_ch_" + i}
                        id={"appr_ch_" + i}
                        checked={user.isApprover}
                        onChange={(e) =>
                          changeRole(user.user_id, "approver", e.target.checked)
                        }
                      />

                      <Form.Check
                        inline
                        className="mb-0"
                        disabled={!user.userdata_created_at}
                        type="checkbox"
                        label="Indexer"
                        key={"ind_ch_" + i}
                        id={"ind_ch_" + i}
                        checked={user.isIndexer}
                        onChange={(e) =>
                          changeRole(user.user_id, "indexer", e.target.checked)
                        }
                      />

                      <Form.Check
                        inline
                        className="mb-0"
                        disabled={!user.userdata_created_at}
                        type="checkbox"
                        label="Curator"
                        key={"cur_ch_" + i}
                        id={"cur_ch_" + i}
                        checked={user.isCurator}
                        onChange={(e) =>
                          changeRole(user.user_id, "curator", e.target.checked)
                        }
                      />

                      <Form.Check
                        inline
                        className="mb-0"
                        disabled={!user.userdata_created_at}
                        type="checkbox"
                        label="Tagger"
                        key={"tagger_ch_" + i}
                        id={"tagger_ch_" + i}
                        checked={!!user.isTagger}
                        onChange={(e) =>
                          changeRole(user.user_id, "tagger", e.target.checked)
                        }
                      />

                      <Form.Check
                        inline
                        className="mb-0"
                        disabled={!user.userdata_created_at}
                        type="checkbox"
                        label="Feature Admin"
                        title="Can curate the Featured scripts list, and nothing else."
                        key={"feat_ch_" + i}
                        id={"feat_ch_" + i}
                        checked={!!user.isFeatureAdmin}
                        onChange={(e) =>
                          changeRole(user.user_id, "featureAdmin", e.target.checked)
                        }
                      />
                    </td>
                    <td className="text-center align-middle">
                      {displayedBenefits.length > 0 ? (
                        <div className="user-admin-benefits">
                          {displayedBenefits.map((benefit) => (
                            <Badge
                              key={benefit.id}
                              bg={benefit.source === "local" ? "info" : "success"}
                              pill
                            >
                              {benefit.display}
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <div className="text-muted small">Defaults</div>
                      )}
                      <Button
                        variant="primary"
                        size="sm"
                        className="mt-2 d-block mx-auto"
                        disabled={!user.userdata_created_at}
                        title="Set or clear this user's local benefit overrides. Unset values inherit from Patreon or the site default."
                        // Always shown, even when there are no extra tags.
                        onClick={() => openBenefitEditor(user)}
                      >
                        Edit Overrides
                      </Button>
                    </td>
                    <td className="ps-2">
                      <Form.Check
                        inline
                        className="mb-0"
                        disabled={!user.userdata_created_at}
                        type="checkbox"
                        label="Blacklisted"
                        key={"black_ch_" + i}
                        id={"black_ch_" + i}
                        checked={user.isBlacklisted}
                        onChange={(e) =>
                          changeRole(user.user_id, "blacklisted", e.target.checked)
                        }
                      />
                    </td>
                    <td>
                      <Stack direction="horizontal" gap={2} className="justify-content-center">
                        {user.email_confirmed_at && user.userdata_created_at && (
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => {
                              setConfirmationMessage(`Send a password reset email to ${user.email}?\n\nThe user will need to click the reset link before it automatically expires.`);
                              setOnConfirm(() => async () => {
                                setShowModal(false);
                                const session = await supabase.auth.getSession();
                                if (!session.data.session) {
                                  showToast("error", `Error sending password reset email to ${user.email}`);
                                  return;
                                }
                                const { data, error } = await supabase.functions.invoke('account-utility', {
                                  body: {
                                    action: 'reset_password',
                                    user_session: session.data.session.access_token,
                                    target_email: user.email,
                                    redirect_to: `${window.location.origin}/newPassword`,
                                  }
                                });
                                if (error || !data) {
                                  console.error(`Error sending password reset email to ${user.email}:`, error);
                                  showToast("error", `Error sending password reset email to ${user.email}`);
                                } else {
                                  showToast("success", `Password reset email sent to ${user.email}`);
                                }
                              });
                              setOnHide(() => () => {
                                setShowModal(false);
                              });
                              setShowModal(true);
                            }}
                          >
                            Reset Password
                          </Button>
                        )}
                        {user.email_confirmed_at && !user.userdata_created_at && (
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => {
                              setConfirmationMessage(`Do you want to activate the account for ${user.email}?\n\nThis will let you grant permissions even though the user hasn't logged in yet.`);
                              setOnConfirm(() => async () => {
                                setShowModal(false);
                                const newUser = await insertNewUser(user.user_id, user.email);
                                if (newUser) {
                                  showToast("success", `Account activated for ${user.email}`);
                                  getUserData();
                                } else {
                                  showToast("error", `Unable to activate account for ${user.email}`);
                                }
                              });
                              setOnHide(() => () => {
                                setShowModal(false);
                              });
                              setShowModal(true);
                            }}
                          >
                            Activate Account
                          </Button>
                        )}
                        {!user.email_confirmed_at && (
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => {
                              setConfirmationMessage(`Send a verification email to ${user.email}?\n\nThe user will need to click the verification link before it automatically expires.`);
                              setOnConfirm(() => async () => {
                                setShowModal(false);
                                const { error } = await supabase.auth.resend({
                                  type: 'signup',
                                  email: user.email,
                                  options: {
                                    emailRedirectTo: `${window.location.origin}/database`,
                                  },
                                });
                                if (error) {
                                  showToast("error", `Error sending verification email to ${user.email}`);
                                } else {
                                  showToast("success", `Verification email sent to ${user.email}`);
                                }
                              });
                              setOnHide(() => () => {
                                setShowModal(false);
                              });
                              setShowModal(true);
                            }}
                          >
                            Send Verification
                          </Button>
                        )}
                        {/* {user.isActive && (
                          <Button
                            size="sm"
                            variant="outline-danger"
                          >
                            Deactivate
                          </Button>
                        )}
                        {!user.isActive && user.userdata_created_at && (
                          <Button
                            size="sm"
                            variant="outline-info"
                          >
                            Reactivate
                          </Button>
                        )} */}
                      </Stack>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      ) : (
        <LoadingRing />
      )}
      <Modal show={!!editingUser} onHide={() => setEditingUser(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Local benefits</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="small text-muted">
            Unset means inherit from Patreon or the site default. Set a value to override for {editingUser?.email}.
          </p>
          {BENEFIT_REGISTRY.map((benefit) => {
            const value = editDraft[benefit.id];
            return (
              <div key={benefit.id} className="mb-3">
                <div className="fw-semibold">{benefit.label}</div>
                {benefit.valueType === "boolean" ? (
                  <Form.Select
                    size="sm"
                    value={value === "unset" ? "unset" : value === true ? "on" : "off"}
                    onChange={(e) => {
                      const next = e.target.value;
                      setEditDraft((prev) => ({
                        ...prev,
                        [benefit.id]: next === "unset" ? "unset" : next === "on",
                      }));
                    }}
                  >
                    <option value="unset">Unset (inherit)</option>
                    <option value="on">On</option>
                    <option value="off">Off</option>
                  </Form.Select>
                ) : (
                  <Form.Control
                    type="number"
                    size="sm"
                    min={0}
                    step={1}
                    placeholder="Unset (inherit)"
                    value={value === "unset" || value === "" ? "" : String(value)}
                    onChange={(e) => {
                      if (e.target.value === "") {
                        setEditDraft((prev) => ({ ...prev, [benefit.id]: "unset" }));
                        return;
                      }
                      const next = Number.parseInt(e.target.value, 10);
                      setEditDraft((prev) => ({
                        ...prev,
                        [benefit.id]: Number.isFinite(next) ? next : "unset",
                      }));
                    }}
                  />
                )}
              </div>
            );
          })}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setEditingUser(null)} disabled={savingOverrides}>
            Cancel
          </Button>
          <Button variant="primary" onClick={saveBenefitEditor} disabled={savingOverrides}>
            {savingOverrides ? "Saving..." : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>
      <ConfirmationModal show={showModal} message={confirmationMessage} onConfirm={onConfirm} onHide={onHide} />
    </>
  );
}

export default UserAdminDashboard;